import { createContext, useCallback, useContext, useEffect, useRef } from "react";
import { BACKEND, api, getToken } from "../lib/api";
import { useAuth } from "./AuthContext";

const RtCtx = createContext(null);

export function RealtimeProvider({ children }) {
  const { user, logout } = useAuth();
  const subs = useRef(new Set());
  const wsRef = useRef(null);

  const subscribe = useCallback((fn) => {
    subs.current.add(fn);
    return () => subs.current.delete(fn);
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let closed = false;
    let retry;
    const connect = () => {
      const ws = new WebSocket(`${BACKEND.replace(/^http/, "ws")}/api/ws?token=${getToken()}`);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        const msg = JSON.parse(e.data);
        if (msg.event === "logout") logout();
        subs.current.forEach((fn) => fn(msg));
      };
      ws.onclose = () => {
        if (!closed) retry = setTimeout(connect, 4000);
      };
    };
    connect();
    const ping = setInterval(() => {
      api.post("/presence/ping").catch(() => {});
      if (wsRef.current?.readyState === 1) wsRef.current.send("ping");
    }, 30000);
    api.post("/presence/ping").catch(() => {});
    return () => {
      closed = true;
      clearTimeout(retry);
      clearInterval(ping);
      wsRef.current?.close();
    };
  }, [user, logout]);

  return <RtCtx.Provider value={{ subscribe }}>{children}</RtCtx.Provider>;
}

export const useRealtime = () => useContext(RtCtx);

export function useRealtimeEvent(handler) {
  const { subscribe } = useRealtime();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    return subscribe((m) => ref.current(m));
  }, [subscribe]);
}
